import { createRequest, get, post, put, del, patch, upload, download, RequestConfig } from './request';

// 示例 1: 创建自定义请求实例
const apiClient = createRequest({
  baseURL: 'https://api.example.com',
  timeout: 15000,
  headers: {
    Authorization: 'Bearer your-token',
  },
  onError: (error) => {
    console.error('API Error:', error);
  },
});

// 示例 2: 基本 GET 请求
async function getUserInfo(userId: string) {
  try {
    const response = await get<{ id: string; name: string; email: string }>(
      `/api/users/${userId}`,
      { include: 'profile' },
      { retry: 3, retryDelay: 1000 },
    );

    if (response.success) {
      console.log('用户信息:', response.data);
      return response.data;
    }
    console.error('获取用户信息失败:', response.message);
  } catch (error) {
    console.error('请求失败:', error);
  }
}

// 示例 3: POST 请求创建资源
async function createUser(userData: { name: string; email: string; password: string }) {
  try {
    const response = await post<{ id: string; name: string; email: string }>(
      '/api/users',
      userData,
      { showError: true },
    );

    if (response.success) {
      console.log('用户创建成功:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('创建用户失败:', error);
  }
}

// 示例 4: PUT 请求更新资源
async function updateUser(userId: string, userData: { name?: string; email?: string }) {
  try {
    const response = await put<{ id: string; name: string; email: string }>(
      `/api/users/${userId}`,
      userData,
    );

    if (response.success) {
      console.log('用户更新成功:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('更新用户失败:', error);
  }
}

// 示例 5: DELETE 请求删除资源
async function deleteUser(userId: string) {
  try {
    const response = await del<{ success: boolean }>(`/api/users/${userId}`);

    if (response.success) {
      console.log('用户删除成功');
      return true;
    }
  } catch (error) {
    console.error('删除用户失败:', error);
  }
}

// 示例 6: PATCH 请求部分更新
async function updateUserProfile(userId: string, profileData: { avatar?: string; bio?: string }) {
  try {
    const response = await patch<{ id: string; avatar: string; bio: string }>(
      `/api/users/${userId}/profile`,
      profileData,
    );

    if (response.success) {
      console.log('用户资料更新成功:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('更新用户资料失败:', error);
  }
}

// 示例 7: 文件上传
async function uploadAvatar(userId: string, file: File) {
  try {
    const response = await upload<{ url: string }>(
      `/api/users/${userId}/avatar`,
      file,
      (progress) => {
        console.log(`上传进度: ${progress}%`);
      },
      { timeout: 30000 },
    );

    if (response.success) {
      console.log('头像上传成功:', response.data.url);
      return response.data;
    }
  } catch (error) {
    console.error('头像上传失败:', error);
  }
}

// 示例 8: 文件下载
async function downloadReport(reportId: string, filename?: string) {
  try {
    await download(
      `/api/reports/${reportId}/download`,
      filename || `report-${reportId}.pdf`,
    );
    console.log('报告下载成功');
  } catch (error) {
    console.error('报告下载失败:', error);
  }
}

// 示例 9: 带认证的请求配置
const authConfig = {
  headers: {
    Authorization: 'Bearer your-token',
  },
  retry: 2,
  retryDelay: 2000,
  showError: true,
};

// 示例 10: 使用自定义请求实例
async function getProtectedData() {
  try {
    const response = await apiClient.get('/api/protected/data', authConfig);
    console.log('受保护的数据:', response.data);
    return response.data;
  } catch (error) {
    console.error('获取受保护数据失败:', error);
  }
}

// 示例 11: 错误处理
async function handleErrors() {
  try {
    const response = await get('/api/nonexistent');
    return response;
  } catch (error: any) {
    switch (error.code) {
      case 401:
        console.error('未授权，请重新登录');
        // 重定向到登录页
        break;
      case 403:
        console.error('权限不足');
        break;
      case 404:
        console.error('资源不存在');
        break;
      case 500:
        console.error('服务器内部错误');
        break;
      default:
        console.error('未知错误:', error.message);
    }
  }
}

// 示例 12: 批量请求
async function batchRequests() {
  const promises = [
    get('/api/users/1'),
    get('/api/users/2'),
    get('/api/users/3'),
  ];

  try {
    const results = await Promise.all(promises);
    console.log('批量请求结果:', results);
    return results;
  } catch (error) {
    console.error('批量请求失败:', error);
  }
}

// 导出示例函数
export {
  getUserInfo,
  createUser,
  updateUser,
  deleteUser,
  updateUserProfile,
  uploadAvatar,
  downloadReport,
  getProtectedData,
  handleErrors,
  batchRequests,
};
